import { useState } from 'react';
import { format } from 'date-fns';
import { Check, LogOut, Monitor, Moon, Sun } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { GithubSyncCard } from '../components/GithubSyncCard';
import { StravaCard } from '../components/StravaCard';
import { RemindersCard } from '../components/RemindersCard';
import { TimeZoneSetting } from '../components/TimeZoneSetting';
import { ExportButton } from '../components/ExportButton';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';
import {
  ACCENT_OPTIONS,
  getAccentPreference,
  getThemePreference,
  setAccentPreference,
  setThemePreference,
  type ThemePreference,
} from '../lib/theme';

const THEME_CHOICES: { id: ThemePreference; label: string; Icon: typeof Sun }[] = [
  { id: 'light', label: 'Light', Icon: Sun },
  { id: 'system', label: 'System', Icon: Monitor },
  { id: 'dark', label: 'Dark', Icon: Moon },
];

/** Formats an ISO date to "Month YYYY", or null if missing/invalid. */
function formatMemberSince(createdAt?: string | null): string | null {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'MMMM yyyy');
}

/** Appearance preferences: theme (light/system/dark) and the accent pack. */
function AppearanceSection() {
  const [theme, setTheme] = useState<ThemePreference>(getThemePreference);
  const [accent, setAccent] = useState<string>(getAccentPreference);

  const chooseTheme = (pref: ThemePreference) => {
    setTheme(pref);
    setThemePreference(pref);
  };

  const chooseAccent = (id: string) => {
    setAccent(id);
    setAccentPreference(id);
  };

  return (
    <section
      aria-label="Appearance"
      className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
    >
      <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Appearance</h2>

      <fieldset>
        <legend className="label">Theme</legend>
        <div className="mt-1 grid grid-cols-3 gap-2" role="radiogroup" aria-label="Theme">
          {THEME_CHOICES.map(({ id, label, Icon }) => {
            const selected = theme === id;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => chooseTheme(id)}
                className={`flex min-h-11 items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors ${FOCUS_RING_CLASSES} ${
                  selected
                    ? 'border-[var(--primary)] bg-[color-mix(in_oklab,var(--primary)_10%,transparent)] text-[var(--text-primary)]'
                    : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--primary)]'
                }`}
              >
                <Icon className="w-4 h-4" aria-hidden />
                {label}
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="mt-5">
        <legend className="label">Accent</legend>
        <div className="mt-1 flex flex-wrap gap-2.5" role="radiogroup" aria-label="Accent color">
          {ACCENT_OPTIONS.map((option) => {
            const selected = accent === option.id;
            return (
              <button
                key={option.id || 'default'}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={option.label}
                title={option.label}
                onClick={() => chooseAccent(option.id)}
                className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-transform active:scale-95 ${FOCUS_RING_CLASSES} ${
                  selected ? 'border-[var(--text-primary)]' : 'border-transparent'
                }`}
                style={{ backgroundColor: option.swatch }}
              >
                {selected && <Check className="w-5 h-5 text-white drop-shadow" aria-hidden />}
              </button>
            );
          })}
        </div>
      </fieldset>
    </section>
  );
}

/** Profile page: identity, appearance preferences, data, and sign out. */
export function Profile() {
  const { user, logout } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
      </div>
    );
  }

  const displayName = user.displayName || user.email?.split('@')[0] || '?';
  const email = user.email;
  const initial = (user.displayName || user.email || '?').charAt(0).toUpperCase();
  const memberSince = formatMemberSince(user.createdAt);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } catch (err) {
      console.error('Failed to sign out:', err);
      setSigningOut(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-24">
      <h1 className="px-1 pt-4 sm:pt-6 pb-2 text-2xl font-display font-semibold text-[var(--text-primary)]">
        Profile
      </h1>

      {/* 1. Identity card */}
      <section
        aria-label="Your account"
        className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
      >
        <div className="flex items-center gap-4">
          <div
            aria-hidden
            className="w-16 h-16 rounded-full bg-gradient-to-br from-[var(--marine-400)] to-[var(--marine-600)] flex items-center justify-center text-white font-bold text-2xl shrink-0"
          >
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-[var(--text-primary)] truncate">
              {displayName}
            </h2>
            <p className="text-sm text-[var(--text-secondary)] truncate">{email}</p>
            {memberSince && (
              <p className="text-xs text-[var(--text-tertiary)] mt-1">Member since {memberSince}</p>
            )}
          </div>
        </div>
      </section>

      {/* 2. Appearance (theme + accent) */}
      <AppearanceSection />

      {/* 3. Time zone (what "today" and reminder times resolve in) */}
      <TimeZoneSetting currentTimeZone={user.timeZone ?? null} />

      {/* 4. Your data (export) */}
      <section
        aria-label="Your data"
        className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
      >
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Your data</h2>
        <ExportButton />
      </section>

      {/* 5. Reminders / notifications (hides itself when the deployment has push off) */}
      <RemindersCard />

      {/* 6. Strava (hides itself when the deployment has the integration off) */}
      <StravaCard />

      {/* 7. Sync to GitHub (hides itself when the deployment has sync off) */}
      <GithubSyncCard />

      {/* 8. Account section (Sign out) */}
      <section aria-label="Account">
        <button
          type="button"
          onClick={handleSignOut}
          disabled={signingOut}
          aria-busy={signingOut}
          className="btn btn-ghost"
        >
          <LogOut className="w-5 h-5 shrink-0" aria-hidden strokeWidth={2} />
          <span>{signingOut ? 'Signing out…' : 'Sign out'}</span>
        </button>
      </section>
    </div>
  );
}
